import React, { useEffect, useRef, useState, useCallback } from "react";
import OpenSeadragon from "openseadragon";
import { Button, Space, Tooltip } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import "antd/dist/reset.css";
import useAnnotationEvents from "../hooks/useAnnotationEvents";
import AnnotationToolPicker from "./AnnotationToolPicker";
import {
  SHAPE_TO_TOOL_NAME,
  SHAPE_LABEL,
  getShapeKey,
  getGeometry,
  computeBboxGeometry,
  isMeaningfulGeometry,
  colorForAnno,
} from "../utils/annotationShapes";
import { showTip, moveTip, hideTip } from "../utils/hoverTooltip";

const SAVED_STROKE = "rgb(0, 255, 0)";
const PREVIEW_STROKE = "rgba(0, 255, 0, 0.85)";
const PREVIEW_FILL = "rgba(0, 255, 0, 0.18)";

const SHAPE_NAMESPACE = "http://www.w3.org/2000/svg";

const computeBoundingBox = (shape, geometry) => {
  if (!geometry) return null;
  if (shape === "rectangle" || shape === "ellipse" || shape === "circle") {
    return { x: geometry.x, y: geometry.y, width: geometry.width, height: geometry.height };
  }
  if (shape === "arrow") {
    const x = Math.min(geometry.x1, geometry.x2);
    const y = Math.min(geometry.y1, geometry.y2);
    const width = Math.abs(geometry.x2 - geometry.x1) || 0.0001;
    const height = Math.abs(geometry.y2 - geometry.y1) || 0.0001;
    return { x, y, width, height };
  }
  if (shape === "freehand") {
    const xs = (geometry.points || []).map((p) => p[0]);
    const ys = (geometry.points || []).map((p) => p[1]);
    if (!xs.length) return null;
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    return { x: minX, y: minY, width: Math.max(maxX - minX, 0.0001), height: Math.max(maxY - minY, 0.0001) };
  }
  return null;
};

const buildShapeSvg = (shape, geometry, bbox, opts = {}) => {
  const svg = document.createElementNS(SHAPE_NAMESPACE, "svg");
  svg.setAttribute("viewBox", "0 0 1 1");
  svg.setAttribute("preserveAspectRatio", "none");
  svg.style.width = "100%";
  svg.style.height = "100%";
  svg.style.overflow = "visible";
  svg.style.pointerEvents = "none";

  // Saved shapes go in a hoverable group (transparent fill = the whole interior
  // is a hover target) carrying a <title> for a native name/description tooltip.
  const group = document.createElementNS(SHAPE_NAMESPACE, "g");
  group.style.pointerEvents = opts.preview ? "none" : "all";
  if (!opts.preview && opts.title) {
    group.style.cursor = "help";
    // Instant custom tooltip (no native <title> delay).
    group.addEventListener("mouseenter", (e) =>
      showTip(opts.title, e.clientX, e.clientY)
    );
    group.addEventListener("mousemove", (e) => moveTip(e.clientX, e.clientY));
    group.addEventListener("mouseleave", hideTip);
  }

  const stroke = opts.preview ? PREVIEW_STROKE : opts.color || SAVED_STROKE;
  const fill = opts.preview ? PREVIEW_FILL : "transparent";
  const dash = opts.preview ? "0.04 0.04" : "";

  const setCommon = (el) => {
    el.setAttribute("stroke", stroke);
    el.setAttribute("fill", fill);
    el.setAttribute("stroke-width", "2");
    el.setAttribute("vector-effect", "non-scaling-stroke");
    if (dash) el.setAttribute("stroke-dasharray", dash);
  };

  const toLocal = (px, py) => {
    const lx = bbox.width === 0 ? 0 : (px - bbox.x) / bbox.width;
    const ly = bbox.height === 0 ? 0 : (py - bbox.y) / bbox.height;
    return [lx, ly];
  };

  if (shape === "rectangle") {
    const r = document.createElementNS(SHAPE_NAMESPACE, "rect");
    r.setAttribute("x", "0");
    r.setAttribute("y", "0");
    r.setAttribute("width", "1");
    r.setAttribute("height", "1");
    setCommon(r);
    group.appendChild(r);
  } else if (shape === "ellipse" || shape === "circle") {
    const e = document.createElementNS(SHAPE_NAMESPACE, "ellipse");
    e.setAttribute("cx", "0.5");
    e.setAttribute("cy", "0.5");
    e.setAttribute("rx", "0.5");
    e.setAttribute("ry", "0.5");
    setCommon(e);
    group.appendChild(e);
  } else if (shape === "arrow") {
    const [lx1, ly1] = toLocal(geometry.x1, geometry.y1);
    const [lx2, ly2] = toLocal(geometry.x2, geometry.y2);
    const line = document.createElementNS(SHAPE_NAMESPACE, "line");
    line.setAttribute("x1", lx1);
    line.setAttribute("y1", ly1);
    line.setAttribute("x2", lx2);
    line.setAttribute("y2", ly2);
    setCommon(line);
    group.appendChild(line);

    const angle = Math.atan2(ly2 - ly1, lx2 - lx1);
    const headLen = 0.18;
    const hX1 = lx2 - headLen * Math.cos(angle - Math.PI / 6);
    const hY1 = ly2 - headLen * Math.sin(angle - Math.PI / 6);
    const hX2 = lx2 - headLen * Math.cos(angle + Math.PI / 6);
    const hY2 = ly2 - headLen * Math.sin(angle + Math.PI / 6);
    const head = document.createElementNS(SHAPE_NAMESPACE, "polyline");
    head.setAttribute("points", `${hX1},${hY1} ${lx2},${ly2} ${hX2},${hY2}`);
    setCommon(head);
    head.setAttribute("fill", "none");
    group.appendChild(head);
  } else if (shape === "freehand") {
    const pts = (geometry.points || [])
      .map(([px, py]) => {
        const [lx, ly] = toLocal(px, py);
        return `${lx},${ly}`;
      })
      .join(" ");
    const poly = document.createElementNS(SHAPE_NAMESPACE, "polyline");
    poly.setAttribute("points", pts);
    setCommon(poly);
    poly.setAttribute("fill", "none");
    group.appendChild(poly);
  }

  svg.appendChild(group);
  return svg;
};

const HistologyViewer = ({ dziUrl = "/images/dzi_output/histo.dzi", annotations = [], targetAnnotationId = null, showAnnotations = true, onAnnotationCompleted }) => {
  const wrapperRef = useRef(null);
  const viewerRef = useRef(null);
  const osdRef = useRef(null);
  const overlaysRef = useRef([]);
  const previewOverlayRef = useRef(null);
  const drawingStateRef = useRef({ start: null, points: [] });
  const [activeShape, setActiveShape] = useState("rectangle");
  const [isDrawing, setIsDrawing] = useState(false);
  const isDrawingRef = useRef(false);
  const activeShapeRef = useRef("rectangle");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  useEffect(() => { isDrawingRef.current = isDrawing; }, [isDrawing]);
  useEffect(() => { activeShapeRef.current = activeShape; }, [activeShape]);

  const removePreviewOverlay = useCallback(() => {
    const viewer = osdRef.current;
    if (viewer && previewOverlayRef.current) {
      try { viewer.removeOverlay(previewOverlayRef.current); } catch (_) {}
    }
    previewOverlayRef.current = null;
  }, []);

  const cleanupHistoRectangle = useCallback(() => {
    removePreviewOverlay();
    drawingStateRef.current = { start: null, points: [] };
  }, [removePreviewOverlay]);

  const cleanupOnExternalEvent = useCallback(() => {
    cleanupHistoRectangle();
    setIsDrawing(false);
  }, [cleanupHistoRectangle]);

  const drawingCompatRef = useRef({ rectangle: null });
  useEffect(() => {
    drawingCompatRef.current.rectangle = previewOverlayRef.current;
  });
  useAnnotationEvents("histology", drawingCompatRef, () => {}, cleanupOnExternalEvent);

  // OSD setup
  useEffect(() => {
    osdRef.current = OpenSeadragon({
      id: `histology-viewer-${Date.now()}-${Math.random()}`,
      element: viewerRef.current,
      tileSources: dziUrl,
      showNavigator: true,
      defaultZoomLevel: 1,
      prefixUrl: "https://openseadragon.github.io/openseadragon/images/",
      showRotationControl: false,
      showHomeControl: false,
      showFullPageControl: false,
      showZoomControl: false,
      showSequenceControl: false,
      navigatorAutoFade: false,
      controlsFadeDelay: 5000,
      controlsFadeLength: 2000,
    });

    const viewer = osdRef.current;

    viewer.addHandler("open", () => {
      if (showAnnotations && annotations.length > 0) renderAnnotations();
    });

    viewer.addHandler("zoom", ({ zoom }) => {
      setZoomLevel(zoom);
    });

    return () => {
      if (viewer) {
        try { viewer.destroy(); } catch (_) {}
        osdRef.current = null;
      }
      overlaysRef.current = [];
      previewOverlayRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drawing handlers when isDrawing
  useEffect(() => {
    const viewer = osdRef.current;
    if (!viewer || !viewer.world) return;

    if (isDrawing) {
      viewer.setMouseNavEnabled(false);
      if (viewer.element) viewer.element.style.cursor = "crosshair";
    } else {
      viewer.setMouseNavEnabled(true);
      if (viewer.element) viewer.element.style.cursor = "grab";
      return;
    }

    const container = viewer.container;
    const drawing = drawingStateRef.current;

    const toViewportPoint = (clientX, clientY) => {
      const rect = container.getBoundingClientRect();
      const mx = clientX - rect.left;
      const my = clientY - rect.top;
      return viewer.viewport.pointFromPixel(new OpenSeadragon.Point(mx, my));
    };

    const computeGeometry = (shape, p1, p2) => {
      if (shape === "rectangle" || shape === "ellipse" || shape === "circle") {
        return computeBboxGeometry(p1, p2);
      }
      if (shape === "arrow") {
        return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
      }
      return null;
    };

    const createOrUpdatePreview = (shape, geometry) => {
      const bbox = computeBoundingBox(shape, geometry);
      if (!bbox) return;
      const svg = buildShapeSvg(shape, geometry, bbox, { preview: true });
      const wrapper = document.createElement("div");
      wrapper.style.width = "100%";
      wrapper.style.height = "100%";
      wrapper.style.pointerEvents = "none";
      wrapper.appendChild(svg);

      if (previewOverlayRef.current) {
        try { viewer.removeOverlay(previewOverlayRef.current); } catch (_) {}
      }
      previewOverlayRef.current = wrapper;
      viewer.addOverlay({
        element: wrapper,
        location: new OpenSeadragon.Rect(bbox.x, bbox.y, bbox.width, bbox.height),
        placement: OpenSeadragon.Placement.TOP_LEFT,
      });
    };

    const handleMouseDown = (event) => {
      const shape = activeShapeRef.current;
      const p = toViewportPoint(event.clientX, event.clientY);
      if (shape === "freehand") {
        drawing.points = [[p.x, p.y]];
        createOrUpdatePreview("freehand", { points: [[p.x, p.y]] });
      } else {
        drawing.start = p;
        const geom = computeGeometry(shape, p, p);
        if (geom) createOrUpdatePreview(shape, geom);
      }
      drawing.isDrawing = true;
      event.preventDefault();
      event.stopPropagation();
    };

    const handleMouseMove = (event) => {
      if (!drawing.isDrawing) return;
      const shape = activeShapeRef.current;
      const p = toViewportPoint(event.clientX, event.clientY);
      if (shape === "freehand") {
        const last = drawing.points[drawing.points.length - 1];
        if (!last || Math.hypot(p.x - last[0], p.y - last[1]) > 0.002) {
          drawing.points.push([p.x, p.y]);
          createOrUpdatePreview("freehand", { points: [...drawing.points] });
        }
      } else {
        const start = drawing.start;
        if (!start) return;
        const geom = computeGeometry(shape, start, p);
        if (geom) createOrUpdatePreview(shape, geom);
      }
      event.preventDefault();
      event.stopPropagation();
    };

    const handleMouseUp = (event) => {
      if (!drawing.isDrawing) return;
      drawing.isDrawing = false;
      const shape = activeShapeRef.current;
      const p = toViewportPoint(event.clientX, event.clientY);
      let finalGeom;
      if (shape === "freehand") {
        finalGeom = { points: [...drawing.points] };
      } else {
        finalGeom = computeGeometry(shape, drawing.start, p);
      }
      if (!isMeaningfulGeometry(shape, finalGeom)) {
        cleanupHistoRectangle();
        setIsDrawing(false);
        return;
      }
      const toolName = SHAPE_TO_TOOL_NAME[shape];
      const annotationData = {
        bounds: shape === "rectangle"
          ? { x: finalGeom.x, y: finalGeom.y, width: finalGeom.width, height: finalGeom.height }
          : undefined,
        geometry: finalGeom,
        toolName,
        timestamp: new Date().toISOString(),
        description: "Added annotation",
        id: `histo-anno-${Date.now()}`,
        name: `${SHAPE_LABEL[shape]} ROI`,
      };
      if (typeof onAnnotationCompleted === "function") {
        onAnnotationCompleted("histo-initial", annotationData);
      }
      // Keep preview visible until parent saves/cancels (handled via custom events)
      drawingStateRef.current = { start: null, points: [] };
      event.preventDefault();
      event.stopPropagation();
    };

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDrawing, onAnnotationCompleted, cleanupHistoRectangle]);

  // Render saved annotations
  const renderAnnotations = useCallback(() => {
    const viewer = osdRef.current;
    if (!viewer || !viewer.world || !showAnnotations) return;

    overlaysRef.current.forEach((overlay) => {
      try { if (viewer.getOverlayById(overlay.element)) viewer.removeOverlay(overlay.element); } catch (_) {}
    });
    overlaysRef.current = [];

    annotations.forEach((anno) => {
      const shape = getShapeKey(anno);
      const geometry = getGeometry(anno);
      if (!shape || !geometry) return;
      const bbox = computeBoundingBox(shape, geometry);
      if (!bbox) return;
      const title = [anno.name, anno.description].filter(Boolean).join(" — ");
      const svg = buildShapeSvg(shape, geometry, bbox, {
        title,
        color: colorForAnno(anno),
      });
      const wrapper = document.createElement("div");
      wrapper.id = `histo-anno-${anno.id}`;
      wrapper.style.width = "100%";
      wrapper.style.height = "100%";
      wrapper.style.pointerEvents = "none";
      wrapper.appendChild(svg);

      try {
        viewer.addOverlay({
          element: wrapper,
          location: new OpenSeadragon.Rect(bbox.x, bbox.y, bbox.width, bbox.height),
          placement: OpenSeadragon.Placement.TOP_LEFT,
        });
        overlaysRef.current.push({ element: wrapper });
      } catch (error) {
        console.error("Error adding overlay for annotation:", anno.id, error);
      }
    });
  }, [annotations, showAnnotations]);

  useEffect(() => {
    const viewer = osdRef.current;
    if (!viewer || !viewer.world) return;
    renderAnnotations();
  }, [renderAnnotations]);

  // Zoom to target annotation
  useEffect(() => {
    const viewer = osdRef.current;
    if (!viewer || !viewer.world || !targetAnnotationId) return;
    const targetAnno = annotations.find((anno) => anno.id === targetAnnotationId);
    if (!targetAnno) return;
    const shape = getShapeKey(targetAnno);
    const geometry = getGeometry(targetAnno);
    const bbox = computeBoundingBox(shape, geometry);
    if (!bbox) return;
    const padding = 0.1;
    const padded = new OpenSeadragon.Rect(
      bbox.x - bbox.width * padding,
      bbox.y - bbox.height * padding,
      bbox.width * (1 + padding * 2),
      bbox.height * (1 + padding * 2)
    );
    viewer.viewport.fitBounds(padded, true);
  }, [targetAnnotationId, annotations]);

  useEffect(() => {
    const onFSChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => osdRef.current?.forceRedraw?.(), 50);
    };
    document.addEventListener("fullscreenchange", onFSChange);
    return () => document.removeEventListener("fullscreenchange", onFSChange);
  }, []);

  // Toolbar handlers
  const handleZoomIn = () => osdRef.current?.viewport.zoomBy(1.2);
  const handleZoomOut = () => osdRef.current?.viewport.zoomBy(0.8);
  const handleReset = () => osdRef.current?.viewport.goHome();
  const handleFullscreen = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    if (!document.fullscreenElement) {
      wrapper.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleShapePick = (shapeKey) => {
    if (isDrawing && shapeKey === activeShape) {
      setIsDrawing(false);
      cleanupHistoRectangle();
      return;
    }
    setActiveShape(shapeKey);
    setIsDrawing(true);
    cleanupHistoRectangle();
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        ref={viewerRef}
        style={{
          position: "absolute",
          inset: 0,
          border: "1px solid #ddd",
          background: "#f0f0f0",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 15,
          left: 15,
          zIndex: 1000,
          pointerEvents: "auto",
        }}
      >
        <Space direction="vertical">
          <Tooltip title="Zoom avant" placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
            />
          </Tooltip>
          <Tooltip title="Zoom arrière" placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
            />
          </Tooltip>
          <Tooltip title="Réinitialiser" placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={<ReloadOutlined />}
              onClick={handleReset}
            />
          </Tooltip>
          <Tooltip title={isFullscreen ? "Quitter le plein écran" : "Plein écran"} placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              className="viewer-tool-btn"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={handleFullscreen}
            />
          </Tooltip>
          <AnnotationToolPicker
            activeShape={activeShape}
            isDrawing={isDrawing}
            onPick={handleShapePick}
            placement="right"
            buttonClassName="viewer-tool-btn"
          />
        </Space>
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 10,
          right: 10,
          zIndex: 1000,
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: 12,
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        ×{zoomLevel.toFixed(1)}
      </div>
    </div>
  );
};

export default HistologyViewer;
